import { Box } from '@mui/material'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Role, type User } from 'core/types'
import { useAuth } from 'hooks/use-auth'
import 'Routes/ProtectedRoute.css'
import { Container } from '@mui/system'
import * as AppRoutes from 'Pages/Routes'

interface Props {
  allow: (user: User) => boolean
  allowAnonymous?: boolean
  noContainer?: boolean
}

export const ProtectedRoute = (props: Props): JSX.Element => {
  const auth = useAuth()
  const nav = useNavigate()
  const route = useLocation()

  if (auth.loading) return <></>

  if ((auth.user === null || auth.user.inactive) && props.allowAnonymous !== true) {
    // returnUrl must be an absolute client-origin URL, not a bare path - the login form posts to
    // the server's own origin, and the server's redirect back to this SPA has no other way to know
    // where the client is hosted (matters most in local dev, where client/server are different origins).
    const clientUrl = process.env.REACT_APP_CLIENT_URL ?? ''
    nav(`${AppRoutes.LOGIN}?returnUrl=${encodeURIComponent(clientUrl + route.pathname)}`)
    return <></>
  }

  let outlet = <Outlet />
  let cnt = outlet
  if (props.noContainer === undefined || !props.noContainer) { cnt = outlet = <Container className='outletContainer'>{outlet}</Container> }

  // Allow access to the page if the user has permission or if the page allows anonymous access and the user is not logged in.
  // In the event that a logged in user does not have the permission to access an anonymous page, they are still
  // considered unauthorized so as to avoid unexpected behavior.
  // Signing in to an invalid account with the "noAccount" flag counts as not signed in for the purposes of the caregiver portal.
  return (props.allowAnonymous === true && (auth.user == null || auth.user.noAccount)) || (auth.user != null && (props.allow(auth.user) || auth.user.securityLevel === Role.MIECC_ADMIN)) ? cnt : <NotAllowed />
}

export const NotAllowed = (): JSX.Element => {
  return (
    <Box justifyContent="center" display={'flex'}>
      You are not authorized to access this page
    </Box>
  )
}
